/**
 * Admin review queue for pending founder applications. Each card can be opened
 * (slide panel) for full applicant detail, rejected, or approved. Approval goes
 * through AssignEIRModal so an EIR can be assigned at the same time.
 *
 * Approve/reject optimistically remove the row from the local list rather than
 * re-fetching the whole queue.
 */
import React, { useEffect, useState } from 'react';
import {
  getAdminApplications, approveApplication, rejectApplication,
  getEIRList, getUserDetail, assignEIR,
} from '../services/apiService';
import SlidePanel from '../components/SlidePanel';

/** Pending-application review queue with approve (+ EIR assign) and reject actions. */
export default function AdminApplications() {
  const [applications, setApplications] = useState([]);
  const [eirs, setEirs]                 = useState([]);
  const [loading, setLoading]           = useState(true);
  const [selected, setSelected]         = useState(null);
  const [approving, setApproving]       = useState(null);

  useEffect(() => {
    Promise.all([getAdminApplications(), getEIRList()])
      .then(([a, e]) => { setApplications(a); setEirs(e); })
      .finally(() => setLoading(false));
  }, []);

  async function openDetail(userId) {
    setSelected({ id: userId, loading: true, data: null });
    const data = await getUserDetail(userId);
    setSelected({ id: userId, loading: false, data });
  }

  async function handleApprove(userId, eirId) {
    await approveApplication(userId, eirId);
    setApplications((prev) => prev.filter((a) => a.id !== userId));
    setApproving(null);
    getEIRList().then(setEirs);
  }

  async function handleReject(userId) {
    await rejectApplication(userId);
    setApplications((prev) => prev.filter((a) => a.id !== userId));
  }

  if (loading) return <div style={s.loading}>Loading…</div>;

  const panelTitle = selected?.data
    ? `${selected.data.first_name} ${selected.data.last_name}`
    : 'Loading…';

  return (
    <div style={s.page} className="page-scroll">
      <div style={s.inner}>
        <div style={s.header}>
          <h1 style={s.title}>Applications</h1>
          {applications.length > 0 && (
            <span style={s.badge}>{applications.length}</span>
          )}
        </div>

        <p style={s.sectionNote}>
          Approving an application assigns the founder to an EIR and grants portal access. Click any row to see full details.
        </p>

        {applications.length === 0 && (
          <div style={s.emptyState}>No pending applications.</div>
        )}

        {applications.map((a) => (
          <AppCard
            key={a.id}
            application={a}
            onSelect={() => openDetail(a.id)}
            onApprove={() => setApproving(a)}
            onReject={() => handleReject(a.id)}
          />
        ))}

        {approving && (
          <AssignEIRModal
            applicant={approving}
            eirs={eirs}
            onConfirm={(eirId) => handleApprove(approving.id, eirId)}
            onCancel={() => setApproving(null)}
          />
        )}
      </div>

      <SlidePanel open={!!selected} onClose={() => setSelected(null)} title={panelTitle}>
        {selected?.loading && <p style={s.panelLoading}>Loading…</p>}
        {selected?.data && <ApplicationDetail detail={selected.data} />}
      </SlidePanel>
    </div>
  );
}

const STAGE_LABELS = {
  idea: 'Idea Stage', mvp: 'MVP / Pre-revenue',
  revenue: 'Early Revenue', scaling: 'Scaling',
};

// Read-only render of a full application. Demographics are collapsed by default
// since they are de-identified and not part of the assessment.
function ApplicationDetail({ detail }) {
  const p = detail.profile || {};
  const [demoOpen, setDemoOpen] = useState(false);

  return (
    <div style={ad.wrap}>

      <div style={ad.section}>
        <p style={ad.sectionLabel}>Contact</p>
        <Row label="Email" value={detail.email} />
        {detail.created_at && <Row label="Applied" value={detail.created_at} />}
        <Row label="Phone" value={p.phone} />
        <Row label="Job title" value={p.job_title} />
      </div>

      {(p.address || p.city || p.county) && (
        <div style={ad.section}>
          <p style={ad.sectionLabel}>Location</p>
          {p.address && (
            <p style={ad.addrBlock}>
              {p.address}{p.address2 ? `, ${p.address2}` : ''}<br />
              {[p.city, p.state, p.zip_code].filter(Boolean).join(', ')}
            </p>
          )}
          <Row label="County" value={p.county} />
          {p.in_capital_region && (
            <div style={ad.row}>
              <span style={ad.label}>Capital Region</span>
              <span style={{
                ...ad.regionBadge,
                background: p.in_capital_region === 'Yes' ? '#dcfce7' : '#fee2e2',
                color: p.in_capital_region === 'Yes' ? '#15803d' : '#b91c1c',
              }}>{p.in_capital_region}</span>
            </div>
          )}
        </div>
      )}

      {p.support_type && (
        <div style={ad.section}>
          <p style={ad.sectionLabel}>Support Needed</p>
          <p style={ad.body}>{p.support_type}</p>
        </div>
      )}

      <div style={ad.section}>
        <p style={ad.sectionLabel}>Company</p>
        <Row label="Company" value={p.company_name} />
        <Row label="Stage" value={STAGE_LABELS[p.company_stage] || p.company_stage} />
        <Row label="Sector" value={p.industry} />
        {p.website && (
          <div style={ad.row}>
            <span style={ad.label}>Website</span>
            {/* Only render as a link for http(s) URLs — guards against a
                javascript:/data: href injected via the public application form. */}
            {/^https?:\/\//i.test(p.website)
              ? <a href={p.website} target="_blank" rel="noreferrer" style={ad.link}>{p.website}</a>
              : <span style={ad.value}>{p.website}</span>}
          </div>
        )}
        {p.product_type && <ChipRow label="Product type" value={p.product_type} />}
        <Row label="Has revenue" value={p.has_revenue} />
        <Row label="Cash invested" value={p.cash_invested} />
        {p.investment_sources && <ChipRow label="Investment sources" value={p.investment_sources} />}
        <Row label="Amount seeking" value={p.amount_seeking} />
        {p.use_of_funds && <ChipRow label="Use of funds" value={p.use_of_funds} />}
        <Row label="Interested in investment" value={p.interested_in_investment} />
        <Row label="In other programs" value={p.in_other_programs} />
      </div>

      {p.description && (
        <div style={ad.section}>
          <p style={ad.sectionLabel}>Value Proposition</p>
          <p style={ad.body}>{p.description}</p>
        </div>
      )}

      {(p.total_headcount || p.capital_region_headcount) && (
        <div style={ad.section}>
          <p style={ad.sectionLabel}>Employment</p>
          <Row label="Total headcount" value={p.total_headcount} />
          <Row label="Capital Region headcount" value={p.capital_region_headcount} />
        </div>
      )}

      {p.how_heard && (
        <div style={ad.section}>
          <p style={ad.sectionLabel}>How They Heard About Us</p>
          <p style={ad.body}>{p.how_heard}</p>
        </div>
      )}

      {(p.is_minority_women_owned || p.is_hispanic_latino || p.race_ethnicity ||
        p.is_veteran_owned || p.age_range || p.household_income) && (
        <div style={ad.section}>
          <button style={ad.demoToggle} onClick={() => setDemoOpen((v) => !v)}>
            {demoOpen ? '▲' : '▼'} Demographics
            <span style={ad.demoNote}> — de-identified, for reporting only</span>
          </button>
          {demoOpen && (
            <div style={{ marginTop: 10 }}>
              <Row label="Minority/women-owned" value={p.is_minority_women_owned} />
              <Row label="Hispanic/Latino origin" value={p.is_hispanic_latino} />
              {p.race_ethnicity && <ChipRow label="Race / ethnicity" value={p.race_ethnicity} />}
              <Row label="Veteran-owned" value={p.is_veteran_owned} />
              <Row label="Age range" value={p.age_range} />
              <Row label="Household income" value={p.household_income} />
            </div>
          )}
        </div>
      )}

      {p.pitch_deck && (
        <div style={ad.section}>
          <p style={ad.sectionLabel}>Pitch Deck</p>
          <div style={ad.deckRow}>
            <span style={ad.deckName}>{p.pitch_deck.filename}</span>
            <span style={ad.deckVersion}>v{p.pitch_deck.version_number}</span>
            <a href={p.pitch_deck.url} target="_blank" rel="noreferrer" style={ad.deckBtn}>Preview</a>
            <a href={p.pitch_deck.url} download={p.pitch_deck.filename} style={ad.deckBtn}>Download</a>
          </div>
        </div>
      )}

    </div>
  );
}

function Row({ label, value }) {
  if (!value) return null;
  return (
    <div style={ad.row}>
      <span style={ad.label}>{label}</span>
      <span style={ad.value}>{value}</span>
    </div>
  );
}

// Renders a comma-joined multi-select value (as stored by the application form)
// as individual chips.
function ChipRow({ label, value }) {
  if (!value) return null;
  const items = value.split(',').map((v) => v.trim()).filter(Boolean);
  if (!items.length) return null;
  return (
    <div style={{ ...ad.row, alignItems: 'flex-start' }}>
      <span style={ad.label}>{label}</span>
      <div style={ad.chips}>
        {items.map((v) => <span key={v} style={ad.chip}>{v}</span>)}
      </div>
    </div>
  );
}

const ad = {
  wrap:         { display: 'flex', flexDirection: 'column', gap: 0 },
  section:      { marginBottom: 24 },
  sectionLabel: { fontFamily: 'var(--font-body)', fontSize: 11, fontWeight: 700, color: 'var(--color-forest)', opacity: 0.5, textTransform: 'uppercase', letterSpacing: '0.06em', marginBottom: 10 },
  row:          { display: 'flex', justifyContent: 'space-between', gap: 12, marginBottom: 8 },
  label:        { fontFamily: 'var(--font-body)', fontSize: 13, color: 'var(--color-forest)', opacity: 0.6, flexShrink: 0 },
  value:        { fontFamily: 'var(--font-body)', fontSize: 13, fontWeight: 600, color: 'var(--color-forest)', textAlign: 'right', flex: 1 },
  link:         { fontFamily: 'var(--font-body)', fontSize: 13, fontWeight: 600, color: 'var(--color-forest)', textAlign: 'right', flex: 1, wordBreak: 'break-all' },
  body:         { fontFamily: 'var(--font-body)', fontSize: 13, color: 'var(--color-forest)', lineHeight: 1.6, opacity: 0.85, background: 'var(--color-mint)', borderRadius: 'var(--radius-sm)', padding: '10px 12px', border: '1px solid var(--color-mint-dark)' },
  addrBlock:    { fontFamily: 'var(--font-body)', fontSize: 13, color: 'var(--color-forest)', lineHeight: 1.7, opacity: 0.85, marginBottom: 8 },
  regionBadge:  { fontFamily: 'var(--font-body)', fontSize: 11, fontWeight: 700, padding: '2px 8px', borderRadius: 9999, whiteSpace: 'nowrap', flexShrink: 0 },
  chips:        { display: 'flex', flexWrap: 'wrap', gap: 4, justifyContent: 'flex-end', flex: 1 },
  chip:         { fontFamily: 'var(--font-body)', fontSize: 11, fontWeight: 600, color: 'var(--color-forest)', background: 'var(--color-mint)', border: '1px solid var(--color-mint-dark)', borderRadius: 9999, padding: '2px 8px', whiteSpace: 'nowrap' },
  demoToggle:   { background: 'none', border: 'none', cursor: 'pointer', fontFamily: 'var(--font-body)', fontSize: 11, fontWeight: 700, color: 'var(--color-forest)', opacity: 0.55, textTransform: 'uppercase', letterSpacing: '0.06em', padding: 0, display: 'inline-flex', alignItems: 'center', gap: 4 },
  demoNote:     { fontFamily: 'var(--font-body)', fontSize: 10, fontWeight: 400, textTransform: 'none', letterSpacing: 0, fontStyle: 'italic', opacity: 0.85 },
  deckRow:      { display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap', padding: '10px 14px', background: 'var(--color-mint)', borderRadius: 'var(--radius-sm)', border: '1px solid var(--color-mint-dark)' },
  deckName:     { flex: 1, fontFamily: 'var(--font-body)', fontSize: 13, fontWeight: 600, color: 'var(--color-forest)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', minWidth: 0 },
  deckVersion:  { fontFamily: 'var(--font-body)', fontSize: 11, fontWeight: 700, color: '#15803d', background: '#dcfce7', padding: '2px 8px', borderRadius: 9999, whiteSpace: 'nowrap', flexShrink: 0 },
  deckBtn:      { fontFamily: 'var(--font-body)', fontSize: 12, fontWeight: 600, color: 'var(--color-forest)', background: '#fff', border: '1.5px solid var(--color-mint-dark)', borderRadius: 'var(--radius-sm)', padding: '4px 10px', cursor: 'pointer', textDecoration: 'none', flexShrink: 0 },
};

function AppCard({ application: a, onSelect, onApprove, onReject }) {
  const [busy, setBusy] = useState(false);

  async function handleReject(e) {
    e.stopPropagation();
    setBusy(true);
    try { await onReject(); } finally { setBusy(false); }
  }

  return (
    <div style={{ ...s.appCard, cursor: 'pointer' }} onClick={onSelect}>
      <div style={s.appInfo}>
        <p style={s.appName}>{a.first_name} {a.last_name}</p>
        <p style={s.appEmail}>{a.email}</p>
      </div>
      <div style={s.appActions}>
        <button style={s.rejectBtn} onClick={handleReject} disabled={busy}>Reject</button>
        <button style={s.approveBtn} onClick={(e) => { e.stopPropagation(); onApprove(); }}>Approve</button>
      </div>
    </div>
  );
}

// Approval confirmation that lets the admin optionally assign an EIR. Passing a
// null EIR id approves without assignment (can be assigned later).
function AssignEIRModal({ applicant, eirs, onConfirm, onCancel }) {
  const [selectedEir, setSelectedEir] = useState('');
  const [saving, setSaving]           = useState(false);

  async function handleConfirm() {
    setSaving(true);
    try { await onConfirm(selectedEir ? parseInt(selectedEir, 10) : null); }
    finally { setSaving(false); }
  }

  return (
    <div style={m.overlay} onClick={onCancel}>
      <div style={m.modal} onClick={(e) => e.stopPropagation()}>
        <h2 style={m.title}>Approve Application</h2>
        <p style={m.subtitle}>
          <strong>{applicant.first_name} {applicant.last_name}</strong>
          <span style={m.email}>{applicant.email}</span>
        </p>

        <div style={m.field}>
          <label style={m.label}>Assign to EIR</label>
          {eirs.length === 0 ? (
            <p style={m.noEirs}>No EIR accounts exist yet. You can assign one later.</p>
          ) : (
            <select style={m.select} value={selectedEir} onChange={(e) => setSelectedEir(e.target.value)}>
              <option value="">— Assign later —</option>
              {eirs.map((eir) => (
                <option key={eir.id} value={eir.id}>
                  {eir.first_name} {eir.last_name} ({eir.founder_count} founder{eir.founder_count !== 1 ? 's' : ''})
                </option>
              ))}
            </select>
          )}
        </div>

        <div style={m.actions}>
          <button style={m.cancelBtn} onClick={onCancel} disabled={saving}>Cancel</button>
          <button style={m.confirmBtn} onClick={handleConfirm} disabled={saving}>
            {saving ? 'Approving…' : 'Approve & Assign'}
          </button>
        </div>
      </div>
    </div>
  );
}

const s = {
  page:         { flex: 1, overflowY: 'auto', background: 'linear-gradient(to bottom, var(--color-frost) 0%, var(--color-mint-dark) 100%) fixed', padding: 'var(--space-8)' },
  inner:        { maxWidth: 720, margin: '0 auto' },
  loading:      { flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center', fontFamily: 'var(--font-body)', color: 'var(--color-forest)' },
  header:       { display: 'flex', alignItems: 'center', gap: 12, marginBottom: 'var(--space-4)', paddingTop: 'var(--space-4)' },
  title:        { fontFamily: 'var(--font-accent)', fontSize: 36, fontWeight: 800, color: 'var(--color-rust)', lineHeight: 1.1 },
  badge:        { minWidth: 24, height: 24, borderRadius: 'var(--radius-full)', background: 'var(--color-rust)', color: '#fff', fontSize: 13, fontWeight: 700, display: 'inline-flex', alignItems: 'center', justifyContent: 'center', padding: '0 8px' },
  sectionNote:  { fontFamily: 'var(--font-body)', fontSize: 13, color: 'var(--color-forest)', opacity: 0.65, lineHeight: 1.5, marginBottom: 'var(--space-5)' },
  appCard:      { background: 'var(--color-frost)', borderRadius: 'var(--radius-md)', padding: '14px 20px', marginBottom: 'var(--space-3)', display: 'flex', alignItems: 'center', justifyContent: 'space-between', boxShadow: 'var(--shadow-card)' },
  appInfo:      {},
  appName:      { fontFamily: 'var(--font-body)', fontSize: 14, fontWeight: 700, color: 'var(--color-forest)', marginBottom: 2 },
  appEmail:     { fontFamily: 'var(--font-body)', fontSize: 13, color: 'var(--color-forest)', opacity: 0.65 },
  appActions:   { display: 'flex', gap: 'var(--space-3)' },
  approveBtn:   { padding: '7px 18px', borderRadius: 'var(--radius-sm)', background: 'var(--color-forest)', color: '#fff', fontFamily: 'var(--font-body)', fontSize: 13, fontWeight: 700, border: 'none', cursor: 'pointer' },
  rejectBtn:    { padding: '7px 18px', borderRadius: 'var(--radius-sm)', background: 'none', color: 'var(--color-overdue)', fontFamily: 'var(--font-body)', fontSize: 13, fontWeight: 600, border: '1.5px solid var(--color-overdue)', cursor: 'pointer' },
  emptyState:   { padding: 'var(--space-8)', textAlign: 'center', fontFamily: 'var(--font-body)', fontSize: 14, color: 'var(--color-forest)', opacity: 0.5, background: 'var(--color-frost)', borderRadius: 'var(--radius-lg)' },
  panelLoading: { fontFamily: 'var(--font-body)', fontSize: 13, color: 'var(--color-forest)', opacity: 0.6 },
};

const m = {
  overlay:   { position: 'fixed', inset: 0, background: 'rgba(33,81,45,0.35)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 300, padding: 24 },
  modal:     { background: 'var(--color-frost)', borderRadius: 'var(--radius-lg)', padding: '28px 28px 24px', width: 360, maxWidth: '90vw', flexShrink: 0, boxShadow: 'var(--shadow-modal)' },
  title:     { fontFamily: 'var(--font-display)', fontSize: 20, fontWeight: 800, color: 'var(--color-forest)', marginBottom: 'var(--space-3)' },
  subtitle:  { display: 'flex', flexDirection: 'column', gap: 2, marginBottom: 'var(--space-5)', fontFamily: 'var(--font-body)', fontSize: 14, fontWeight: 700, color: 'var(--color-forest)' },
  email:     { fontWeight: 400, fontSize: 13, opacity: 0.65 },
  field:     { marginBottom: 'var(--space-6)' },
  label:     { display: 'block', fontFamily: 'var(--font-body)', fontSize: 13, fontWeight: 600, color: 'var(--color-forest)', marginBottom: 8 },
  select:    { width: '100%', padding: '9px 12px', borderRadius: 'var(--radius-sm)', border: '1.5px solid var(--color-mint-dark)', background: '#fff', fontFamily: 'var(--font-body)', fontSize: 13, color: 'var(--color-forest)' },
  noEirs:    { fontFamily: 'var(--font-body)', fontSize: 13, color: 'var(--color-forest)', opacity: 0.6, fontStyle: 'italic' },
  actions:   { display: 'flex', justifyContent: 'flex-end', gap: 'var(--space-3)' },
  cancelBtn: { padding: '9px 20px', borderRadius: 'var(--radius-sm)', background: 'none', color: 'var(--color-forest)', fontFamily: 'var(--font-body)', fontSize: 13, fontWeight: 600, border: '1.5px solid var(--color-mint-dark)', cursor: 'pointer' },
  confirmBtn:{ padding: '9px 20px', borderRadius: 'var(--radius-sm)', background: 'var(--color-forest)', color: '#fff', fontFamily: 'var(--font-body)', fontSize: 13, fontWeight: 700, border: 'none', cursor: 'pointer' },
};
